import { AfterViewInit, Component, ElementRef, viewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';

import { FormlyFieldBaseComponent } from '../formly-field-base.component';

/**
 * An email-recipient row (To / Cc / Bcc): addresses as removable chips plus free typing.
 * Enter, comma or semicolon turns what has been typed into a chip; an address that does not
 * parse shows as an INVALID (red) chip and fails the field's validator, so Send can be blocked
 * with the offending entry named.
 *
 * ⚠️ The model value is a plain COMMA-SEPARATED string of addresses — exactly what the API's
 * mail sender accepts — so the field is a drop-in for the text fields it replaces. Chips are
 * presentation only; a contact's name on a chip comes from `props.recipientName`.
 *
 * `props.actions` renders small link-buttons at the right of the row (the email modal's
 * "Contacts…" and "Bcc" links).
 */
@Component({
  selector: 'app-formly-recipients',
  templateUrl: './formly-recipients.component.html',
  styleUrl: './formly-recipients.component.scss',
  imports: [FormsModule, ReactiveFormsModule, FormlyModule],
})
export class FormlyRecipientsComponent extends FormlyFieldBaseComponent implements AfterViewInit {
  private readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('recipientInput');

  protected chips: { email: string; name: string; valid: boolean }[] = [];
  protected pending = '';

  /** The control value the chips currently show — our own writes echo back through
   *  valueChanges, and without this guard they would rebuild the chips mid-typing. */
  private reflected: string;

  ngAfterViewInit() {
    this.loadControlValue();

    // ⚠️ The model arrives AFTER this runs when the form's model is set by the opener (the
    // email modal's prefill) — same shape as formly-rich-text waiting for its narrative.
    this.formControl.valueChanges.pipe(this.untilDestroyed()).subscribe((value: string) => {
      if ((value ?? '') !== this.reflected) {
        this.loadControlValue();
      }
    });
  }

  protected onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      // Enter must make a chip, never submit the surrounding form.
      event.preventDefault();
      this.commitPending();
    }
  }

  protected onBlur() {
    this.commitPending();
  }

  protected remove(chip: { email: string }) {
    this.chips = this.chips.filter((x) => x.email !== chip.email);
    this.writeControlValue();
  }

  protected focusInput() {
    this.inputElement()?.nativeElement.focus();
  }

  /** Whether one address parses — mirrored by the field validator the helper attaches. */
  static isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /** The entries in a stored value (comma or semicolon separated), blanks dropped. */
  static split(value: string): string[] {
    return (value ?? '')
      .split(/[,;]/)
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
  }

  private commitPending() {
    const typed = FormlyRecipientsComponent.split(this.pending);
    if (!typed.length) {
      return;
    }

    this.pending = '';
    for (const email of typed) {
      if (!this.chips.some((x) => x.email.toLowerCase() === email.toLowerCase())) {
        this.chips.push(this.toChip(email));
      }
    }
    this.writeControlValue();
  }

  private loadControlValue() {
    const value: string = this.formControl.value ?? '';
    this.reflected = value;
    this.chips = FormlyRecipientsComponent.split(value).map((email) => this.toChip(email));
  }

  private toChip(email: string) {
    return {
      email,
      name: this.props.recipientName?.(email) || email,
      valid: FormlyRecipientsComponent.isValidEmail(email),
    };
  }

  private writeControlValue() {
    const value = this.chips.map((x) => x.email).join(',');
    this.reflected = value;
    this.formControl.setValue(value);
    this.formControl.markAsDirty();
  }
}
